import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Calendar, Clock, Eye } from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { Class, ClassSchedule } from '../types';
import toast from 'react-hot-toast';

const ClassesPage: React.FC = () => {
  const navigate = useNavigate();
  const { classes, addClass, updateClass, deleteClass, loading } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schedule: [] as ClassSchedule[]
  });

  const daysOfWeek = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  // Normalize schedule that might arrive as a JSON string from the API
  const normalizeSchedule = (schedule: unknown): ClassSchedule[] => {
    if (Array.isArray(schedule)) return schedule as ClassSchedule[];
    if (typeof schedule === 'string') {
      try {
        const parsed = JSON.parse(schedule || '[]');
        return Array.isArray(parsed) ? parsed as ClassSchedule[] : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a class name');
      return;
    }

    try {
      if (editingClass) {
        await updateClass(editingClass.id, formData);
        toast.success('Class updated successfully');
      } else {
        await addClass(formData);
        toast.success('Class created successfully');
      }
      
      setShowModal(false);
      setEditingClass(null);
      setFormData({ name: '', description: '', schedule: [] });
    } catch (error) {
      toast.error('Failed to save class');
    }
  };

  const handleEdit = (classItem: Class) => {
    setEditingClass(classItem);
    setFormData({
      name: classItem.name,
      description: classItem.description || '',
      schedule: normalizeSchedule(classItem.schedule as unknown)
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this class? This action cannot be undone.')) {
      try {
        await deleteClass(id);
        toast.success('Class deleted successfully');
      } catch (error) {
        toast.error('Failed to delete class');
      }
    }
  };

  const addScheduleItem = () => {
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, {
        dayOfWeek: 0,
        startTime: '09:00',
        endTime: '10:00',
        isActive: true
      }]
    }));
  };

  const updateScheduleItem = (index: number, field: keyof ClassSchedule, value: any) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeScheduleItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', schedule: [] });
    setEditingClass(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Classes</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Manage your classes and schedules</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Class
          </button>
        </div>

        {classes.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No classes</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new class.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowModal(true)}
                className="btn-primary"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Class
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map((classItem) => (
              <div key={classItem.id} className="card hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{classItem.name}</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/classes/${classItem.id}`)}
                      className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(classItem)}
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      title="Edit class"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(classItem.id)}
                      className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                      title="Delete class"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {classItem.description && (
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{classItem.description}</p>
                )}
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Schedule:</h4>
                  {normalizeSchedule(classItem.schedule as unknown).length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No schedule set</p>
                  ) : (
                    <div className="space-y-1">
                      {normalizeSchedule(classItem.schedule as unknown)
                        .filter(s => s.isActive)
                        .map((schedule, index) => (
                          <div key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                            <Calendar className="h-4 w-4 mr-2" />
                            <span className="font-medium">{daysOfWeek[schedule.dayOfWeek]}</span>
                            <Clock className="h-4 w-4 ml-2 mr-1" />
                            <span>{schedule.startTime} - {schedule.endTime}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {editingClass ? 'Edit Class' : 'Add New Class'}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Class Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="input-field bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Enter class name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="input-field bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      rows={3}
                      placeholder="Enter class description"
                    />
                  </div>
                  
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Schedule
                      </label>
                      <button
                        type="button"
                        onClick={addScheduleItem}
                        className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        + Add Schedule
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {formData.schedule.map((schedule, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700">
                          <select
                            value={schedule.dayOfWeek}
                            onChange={(e) => updateScheduleItem(index, 'dayOfWeek', parseInt(e.target.value))}
                            className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            {daysOfWeek.map((day, dayIndex) => (
                              <option key={dayIndex} value={dayIndex}>{day}</option>
                            ))}
                          </select>
                          
                          <input
                            type="time"
                            value={schedule.startTime}
                            onChange={(e) => updateScheduleItem(index, 'startTime', e.target.value)}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          
                          <input
                            type="time"
                            value={schedule.endTime}
                            onChange={(e) => updateScheduleItem(index, 'endTime', e.target.value)}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          
                          <button
                            type="button"
                            onClick={() => removeScheduleItem(index)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      {editingClass ? 'Update' : 'Create'} Class
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClassesPage;
